import { projectImages } from '../assets/projects'

export interface Project {
  id: number
  name: string
  url: string
  category: string
  description: string
  image: string
  alt: string
  color?: string
  /** What I owned on this project (all of them: end to end). */
  role: string
  /** Stack actually used — surfaced as hex chips on the dossier page. */
  stack: string[]
  /** Case-study paragraphs for /projects/[slug] — problem → build → outcome. */
  dossier: string[]
}

export const projects: Project[] = [
  {
    id: 1,
    name: 'Noxiy',
    url: 'https://noxiy.com',
    category: 'Software House',
    description: 'Brand platform for a software house — cinematic scroll storytelling that sells engineering credibility',
    image: projectImages['noxiy.webp'],
    alt: 'Noxiy software house homepage showcasing innovative tech solutions and branding',
    color: '',
    role: 'End-to-end — design, build & deployment',
    stack: ['JavaScript', 'GSAP · ScrollTrigger', 'ScrollSmoother', 'Swiper', 'SEO'],
    dossier: [
      'Noxiy is a software house shipping apps and websites — and a software house is judged by its own site before a single portfolio piece is opened. The brief was credibility: a brand platform that feels engineered, not templated.',
      'I designed and built the entire site solo, from first wireframe to production deployment. The experience runs on GSAP ScrollSmoother and ScrollTrigger for cinematic, scroll-driven storytelling, with Swiper-powered showcases for the work and services sections — all tuned to stay smooth on mid-range hardware.',
      'Delivered as a fast, fully responsive static build with on-page SEO groundwork (semantic structure, meta/OG coverage), so the site performs in search as well as it performs on screen.',
    ],
  },
  {
    id: 2,
    name: 'Loop Dimension',
    url: 'https://loopdimension.com',
    category: 'Tech Company',
    description: 'Corporate platform for a global software company — tailored solutions, told with motion',
    image: projectImages['loop-dimension.webp'],
    alt: 'Loop Dimension tech company website featuring scalable software products and services',
    color: '#02d9f4',
    role: 'End-to-end — design, build & deployment',
    stack: ['JavaScript', 'GSAP', 'Swiper', 'Responsive UI', 'SEO'],
    dossier: [
      'Loop Dimension is a global software company building tailored products for international clients. Its web presence had to communicate scale and polish to enterprise buyers across markets.',
      'I built the corporate platform end to end: information architecture, visual design, then a motion-driven front-end using GSAP scroll choreography and Swiper product showcases, fully responsive from phone to widescreen.',
      'The result is a fast marketing platform the company runs its business development on — deployed, monitored, and iterated in production.',
    ],
  },
  {
    id: 3,
    name: 'opsera',
    url: 'https://opsera.org',
    category: 'Cloud Platform',
    description: 'Platform-engineering & cloud-native consultancy site — AWS, Azure, GCP, GenAI, DevSecOps',
    image: projectImages['opsera.webp'],
    alt: 'Opsera cloud platform dashboard and landing page for enterprise cloud solutions',
    color: '#01f6ca',
    role: 'End-to-end — design, build & deployment',
    stack: ['JavaScript', 'GSAP', 'Swiper', 'SEO', 'Cloud'],
    dossier: [
      'OPSERA is a platform-engineering consultancy helping enterprises build secure, cloud-native platforms across AWS, Azure, GCP, OCI and Alibaba Cloud — with GenAI and DevSecOps practices in the portfolio.',
      'Consultancies in this space sell trust, so the site had to read like infrastructure: precise, structured, fast. I designed and shipped the whole platform solo — service architecture pages, capability breakdowns, and GSAP-driven motion that stays out of the content’s way.',
      'Shipped as a performance-first responsive build with full on-page SEO, deployed and maintained in production.',
    ],
  },
  {
    id: 4,
    name: 'lunexa-ioe',
    url: 'https://www.lunexa-ioe.com/',
    category: 'IoE Platform',
    description: 'Next.js platform for an Internet-of-Everything company — IoT fleets, cloud & AI for Gulf businesses',
    image: projectImages['lunexa-ioe.webp'],
    alt: 'Lunexa IoE platform interface connecting Internet of Everything devices and data',
    color: '#4f39f6',
    role: 'End-to-end — design, build & deployment',
    stack: ['Next.js', 'React', 'SSR / SEO', 'Responsive UI'],
    dossier: [
      'Lunexa delivers IoT fleet platforms, cloud infrastructure, AI systems and digital growth services to businesses across the Gulf. The company needed a web platform that could carry that breadth without reading like a generic agency site.',
      'I built it on Next.js with React — server-rendered for search visibility in a competitive regional market, with dedicated service architecture for each line of business (IoT, cloud, full-stack, AI, marketing).',
      'Designed, developed and deployed end to end: from brand-level visual language down to the rendering strategy that gets each service page indexed and ranking.',
    ],
  },
  {
    id: 5,
    name: 'A-sic',
    url: 'https://a-sic.vercel.app/',
    category: 'Cryptocurrency',
    description: 'Investment & crypto platform site with interactive maps and portfolio visualizations',
    image: projectImages['a-sic.webp'],
    alt: 'A-sic cryptocurrency portfolio tracker showing real-time coin prices and charts',
    color: '#ffba00',
    role: 'End-to-end — design, build & deployment',
    stack: ['JavaScript', 'Bootstrap 5', 'Leaflet Maps', 'Data Viz'],
    dossier: [
      'A-SIC is an investment platform in the cryptocurrency and diversified-assets space — a domain where the interface has to make abstract numbers feel tangible and trustworthy.',
      'I built the platform’s web presence with interactive Leaflet-powered maps, animated progress and portfolio visualizations, and a component system on Bootstrap 5 for rapid, consistent layout across its sections.',
      'Full ownership from concept to deployment, tuned for clarity: market data and portfolio views that read at a glance.',
    ],
  },
  {
    id: 6,
    name: 'Byond Media',
    url: 'https://byond-media.vercel.app',
    category: 'Video Production',
    description: 'Showreel-led platform for an elite video production house — the work plays first',
    image: projectImages['byond-media.webp'],
    alt: 'Byond Media video production house website with cinematic showreel and portfolio',
    color: '#3db5dd',
    role: 'End-to-end — design, build & deployment',
    stack: ['JavaScript', 'Bootstrap', 'Owl Carousel', 'Lightbox UI'],
    dossier: [
      'Byond Media is a video production house — which means the site’s only real job is to get out of the way and let the reel sell. Design decisions were subordinated to playback: dark canvas, minimal chrome, work first.',
      'I built a showreel-led experience with carousel-driven portfolio rows and lightbox playback for full-screen viewing, structured so new productions slot into the grid without a rebuild.',
      'Designed, developed and deployed solo — a fast, cinematic storefront for a company whose product is motion.',
    ],
  },
  {
    id: 7,
    name: 'Houzit',
    url: 'https://www.houzit.net/',
    category: 'Furniture E-commerce',
    description: 'Full furniture e-commerce storefront — catalog, product experience and checkout flow',
    image: projectImages['houzit.webp'],
    alt: 'Houzit furniture e-commerce platform displaying modern home furnishing products',
    color: '#964B00',
    role: 'End-to-end — design, build & deployment',
    stack: ['E-commerce', 'Custom Storefront', 'Catalog & Checkout', 'Responsive UI'],
    dossier: [
      'Houzit is a furniture e-commerce platform for modern home furnishing — a product category where purchase decisions are visual, so the storefront had to present furniture the way a showroom does.',
      'I built the full commerce experience: browsable catalog with category navigation, image-forward product pages, and a cart-to-checkout flow — designed and engineered end to end.',
      'Shipped as a complete, responsive storefront in production, from the first design mock to deployment.',
    ],
  },
  {
    id: 8,
    name: 'makersgate',
    url: 'https://www.makersgate.cloud',
    category: 'Instant Manufacturing',
    description: 'Instant-quoting manufacturing platform — upload a part, preview it in 3D, get a price, pay',
    image: projectImages['makersgate.webp'],
    alt: 'Makersgate instant manufacturing platform for on-demand production services',
    color: '#00cfd1',
    role: 'End-to-end — product design, full-stack build & deployment',
    stack: ['React', 'Redux', 'Three.js', 'Stripe', 'REST API', 'i18n'],
    dossier: [
      'MakersGate turns manufacturing into an instant transaction: upload a part, get a live quote for 3D printing, laser cutting or CNC machining, and pay — no email chains, no days of waiting for an estimate. It’s the most product-heavy build in this portfolio.',
      'The platform is a full React application with Redux state management: an in-browser Three.js viewer renders the customer’s uploaded model, a quoting flow prices it against process and material options over a REST API, and Stripe handles payment. Dashboards chart orders and activity, and the whole product ships multilingual (i18n).',
      'I owned it from zero to deploy — product design, front-end architecture, API integration, payments, and production infrastructure.',
    ],
  },
  {
    id: 9,
    name: 'Unlimited EG',
    url: 'https://unlimited-eta.vercel.app/',
    category: 'EdTech',
    description: 'Learning & development platform — courses, progress dashboards and motion-first UI',
    image: projectImages['unlimited-eg.webp'],
    alt: 'Unlimited EG educational technology platform for learning and professional development',
    color: '#9333ea',
    role: 'End-to-end — design, full-stack build & deployment',
    stack: ['React', 'Framer Motion', 'Three.js', 'Charts', 'REST API'],
    dossier: [
      'Unlimited EG is a learning and development platform for the Egyptian market — built to make structured learning feel like a product people want to open, not homework.',
      'It’s a React application with Framer Motion driving the interaction layer, Three.js accents for depth, and chart-based progress dashboards fed over a REST API — so learners see their trajectory, not just a course list.',
      'Designed, developed and deployed end to end as a single owner: UX, front-end architecture, API wiring and production hosting.',
    ],
  },
]

/** URL-safe slug from a project name, e.g. "Loop Dimension" → "loop-dimension". */
export const slugify = (s: string): string =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

export const projectSlug = (p: Project): string => slugify(p.name)

export const getProjectBySlug = (slug: string): Project | undefined =>
  projects.find((p) => slugify(p.name) === slug)
